import type {
  ActivityStatsResponse,
  AttemptEndRequest,
  AttemptStartRequest,
  LevelStatsResponse,
  ProgressResponse,
  RewardGrantRequest,
  SeedProgressRequest,
} from '@arrows/shared';
import type { AuthClient, Fetcher } from './auth';

export class SyncUnavailableError extends Error {}

export class SyncHttpError extends Error {
  constructor(readonly status: number) {
    super(`Progress API returned ${status}.`);
  }
}

function isProgressResponse(value: unknown): value is ProgressResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ProgressResponse>;
  return Number.isInteger(candidate.highestClearedLevel)
    && Number(candidate.highestClearedLevel) >= 0
    && Number.isInteger(candidate.totalWins)
    && Number(candidate.totalWins) >= 0;
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function isLevelStatsResponse(value: unknown): value is LevelStatsResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<LevelStatsResponse>;
  return isNullableNumber(candidate.clearRate)
    && isNullableNumber(candidate.attemptCount)
    && isNullableNumber(candidate.clearTimeP50Ms)
    && isNullableNumber(candidate.clearTimeP90Ms)
    && (candidate.yourPercentile === undefined || isNullableNumber(candidate.yourPercentile));
}

function isActivityStatsResponse(value: unknown): value is ActivityStatsResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<ActivityStatsResponse>;
  return isNullableNumber(candidate.activeUsers1h)
    && isNullableNumber(candidate.activeUsers24h)
    && isNullableNumber(candidate.clears1h)
    && isNullableNumber(candidate.clears24h)
    && (candidate.updatedAt === null || typeof candidate.updatedAt === 'string');
}

export class ProgressApi {
  constructor(
    private readonly baseUrl: string,
    private readonly auth: AuthClient,
    // 素の fetch を保持すると this がインスタンスになり、ブラウザでは Illegal invocation になる
    private readonly fetcher: Fetcher = (input, init) => fetch(input, init),
  ) {}

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    let token = await this.auth.getSessionToken();
    if (token === null) throw new SyncUnavailableError('Authentication is unavailable.');
    const send = () => this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      headers: { ...init.headers, authorization: `Bearer ${token}` },
    });
    let response = await send();
    if (response.status === 401) {
      this.auth.invalidate();
      token = await this.auth.getSessionToken(true);
      if (token === null) return response;
      response = await send();
    }
    return response;
  }

  private post(path: string, body?: unknown): Promise<Response> {
    return this.request(path, {
      method: 'POST',
      ...(body === undefined ? {} : {
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    });
  }

  sendStart(request: AttemptStartRequest): Promise<Response> {
    return this.post('/v1/me/attempts', request);
  }

  sendEnd(attemptId: string, request: AttemptEndRequest): Promise<Response> {
    return this.post(`/v1/me/attempts/${encodeURIComponent(attemptId)}/end`, request);
  }

  sendGameOver(attemptId: string): Promise<Response> {
    return this.post(`/v1/me/attempts/${encodeURIComponent(attemptId)}/game-over`);
  }

  sendReward(request: RewardGrantRequest): Promise<Response> {
    return this.post('/v1/me/rewards', request);
  }

  async getProgress(): Promise<ProgressResponse> {
    const response = await this.request('/v1/me/progress');
    if (!response.ok) throw new SyncHttpError(response.status);
    const parsed: unknown = await response.json();
    if (!isProgressResponse(parsed)) throw new SyncUnavailableError('Invalid progress response.');
    return parsed;
  }

  /**
   * clearTimeMs を渡した回だけサーバーが yourPercentile を返す。集計が空の
   * レベルでは各値が null で返るため、呼び出し側で表示可否を判断する。
   */
  async getLevelStats(level: number, clearTimeMs: number | null): Promise<LevelStatsResponse> {
    const query = clearTimeMs === null
      ? ''
      : `?clearTimeMs=${encodeURIComponent(String(Math.max(0, Math.round(clearTimeMs))))}`;
    const response = await this.request(
      `/v1/stats/levels/${encodeURIComponent(String(level))}${query}`,
    );
    if (!response.ok) throw new SyncHttpError(response.status);
    const parsed: unknown = await response.json();
    if (!isLevelStatsResponse(parsed)) throw new SyncUnavailableError('Invalid level stats.');
    return parsed;
  }

  async getActivityStats(): Promise<ActivityStatsResponse> {
    const response = await this.request('/v1/stats/activity');
    if (!response.ok) throw new SyncHttpError(response.status);
    const parsed: unknown = await response.json();
    if (!isActivityStatsResponse(parsed)) throw new SyncUnavailableError('Invalid activity stats.');
    return parsed;
  }

  async seedProgress(highestClearedLevel: number): Promise<ProgressResponse | null> {
    const request: SeedProgressRequest = { highestClearedLevel };
    const response = await this.post('/v1/me/progress:seed', request);
    if (response.status === 409) return null;
    if (!response.ok) throw new SyncHttpError(response.status);
    const parsed: unknown = await response.json();
    if (!isProgressResponse(parsed)) throw new SyncUnavailableError('Invalid progress response.');
    return parsed;
  }
}
